import { useCallback, useEffect, useState } from 'react';
import { useChangeManager } from '../context/ChangeManagerContext';
import { useReservationData } from '../context/ReservationDataContext';
import { pusherService } from '../services/pusherService';

const LINE_API_URL = process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php';
const API_KEY = 'nc-line-sync-key-2024';

export interface RecentSummaryResult {
    summary: string;
    sourceCounts: { change_history: number; ai_actions: number };
    logId: number | null;
}

interface UseRecentSummaryReturn {
    isLoading: boolean;
    summary: string | null;
    sourceCounts: { change_history: number; ai_actions: number } | null;
    generatedAt: string | null;
    error: string | null;
    generate: () => Promise<void>;
    loadLatest: () => Promise<void>;
    reset: () => void;
}

function toYmd(dateStr: string | undefined | null): string {
    if (!dateStr) return '';
    return String(dateStr).substring(0, 10);
}

export function useRecentSummary(): UseRecentSummaryReturn {
    const { history } = useChangeManager();
    const { reservations, vehicles } = useReservationData();

    const [isLoading, setIsLoading] = useState(false);
    const [summary, setSummary] = useState<string | null>(null);
    const [sourceCounts, setSourceCounts] = useState<{ change_history: number; ai_actions: number } | null>(null);
    const [generatedAt, setGeneratedAt] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const reset = useCallback(() => {
        setSummary(null);
        setSourceCounts(null);
        setGeneratedAt(null);
        setError(null);
    }, []);

    const loadLatest = useCallback(async () => {
        try {
            const res = await fetch(
                `${LINE_API_URL}?action=get_latest_recent_summary&api_key=${API_KEY}`,
                { method: 'GET' }
            );
            const json = await res.json();
            if (!json.success) return;
            if (json.data) {
                setSummary(json.data.summary || '');
                setSourceCounts(json.data.source_counts || { change_history: 0, ai_actions: 0 });
                setGeneratedAt(json.data.created_at || null);
            }
        } catch (e) {
            // サイレント失敗（起動時の自動ロードのため）
        }
    }, []);

    const generate = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            // 本日日付（JST）
            const now = new Date();
            const today = now.toDateString();
            const todayYmd = [
                now.getFullYear(),
                String(now.getMonth() + 1).padStart(2, '0'),
                String(now.getDate()).padStart(2, '0'),
            ].join('-');

            // 本日 pickup または return の予約IDと、それに紐づく車両ID
            const todayReservationIds = new Set<string>();
            const todayVehicleIds = new Set<string>();
            (reservations || []).forEach((r: any) => {
                const pickup = toYmd(r.start_date);
                const ret = toYmd(r.end_date);
                if (pickup === todayYmd || ret === todayYmd) {
                    if (r.id != null) todayReservationIds.add(String(r.id));
                    if (r.reservation_id != null) todayReservationIds.add(String(r.reservation_id));
                    if (r.vehicle_id != null) todayVehicleIds.add(String(r.vehicle_id));
                }
            });

            // 変更履歴から本日分 & 対象予約/車両のみ抽出
            const dataA = (history || [])
                .filter((ch: any) => {
                    if (!ch.timestamp) return false;
                    const d = new Date(ch.timestamp);
                    if (d.toDateString() !== today) return false;
                    const eid = String(ch.entityId ?? '');
                    if (ch.entityType === 'reservation') {
                        return todayReservationIds.has(eid);
                    }
                    if (ch.entityType === 'vehicle') {
                        return todayVehicleIds.has(eid);
                    }
                    return false;
                })
                .map((ch: any) => {
                    const vehicleName = ch.entityType === 'vehicle'
                        ? (vehicles[String(ch.entityId)]?.name || vehicles[String(ch.entityId)]?.plate_number || '')
                        : undefined;
                    return {
                        id: ch.id,
                        timestamp: ch.timestamp,
                        entity_type: ch.entityType,
                        entity_id: ch.entityId,
                        change_type: ch.changeType,
                        source: ch.source,
                        summary: ch.summary,
                        changes: (ch.changes || []).map((c: any) => ({
                            field: c.field,
                            label: c.label,
                            old_value: c.oldValue,
                            new_value: c.newValue,
                        })),
                        vehicle_name: vehicleName,
                    };
                });

            const res = await fetch(
                `${LINE_API_URL}?action=generate_recent_summary&api_key=${API_KEY}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ data_a: dataA }),
                }
            );

            const json = await res.json();
            if (!json.success) {
                throw new Error(json.error || '要約生成に失敗しました');
            }

            setSummary(json.summary || '');
            setSourceCounts(json.source_counts || { change_history: 0, ai_actions: 0 });
            setGeneratedAt(json.created_at || new Date().toISOString());
        } catch (e: any) {
            setError(e?.message || String(e));
        } finally {
            setIsLoading(false);
        }
    }, [history, reservations, vehicles]);

    // 初回マウント時に最新の要約をロード
    useEffect(() => {
        loadLatest();
    }, [loadLatest]);

    // Pusher: 他端末で要約が生成されたら反映
    useEffect(() => {
        const unsubscribe = pusherService.subscribe((eventName: string, data: any) => {
            if (eventName !== 'recent_summary.generated') return;
            if (data?.summary) {
                setSummary(data.summary);
                setSourceCounts(data.source_counts || { change_history: 0, ai_actions: 0 });
                setGeneratedAt(data.created_at || new Date().toISOString());
                setError(null);
            }
        });
        return unsubscribe;
    }, []);

    return { isLoading, summary, sourceCounts, generatedAt, error, generate, loadLatest, reset };
}
